import React, {ReactNode, Suspense} from 'react';
import {observer} from 'mobx-react-lite';
import intl from "react-intl-universal";
import {useRootStore, useUserState} from "@stores/AppStateContext";

import {SectionMessage} from '../../components/SectionMessage';
import {LoadingCircle} from "../../components/LoadingCircle";
import {Loading} from '../../components/Loading';

import PageSurface from "./PageSurface";

const Profile = React.lazy(() => import("../../screens/Profile"));
const WsMembers = React.lazy(() => import("../../screens/WorkspaceSettings/WsMembers"));
const Chartflow = React.lazy(() => import("../../screens/Chartflow"));
const PlansAndBilling = React.lazy(() => import("../../screens/PlansAndBilling"));
const Members = React.lazy(() => import("../../screens/Members"));
const Settings = React.lazy(() => import("../../screens/Settings/Settings"));
const WorkspaceSettings = React.lazy(() => import("../../screens/WorkspaceSettings/WorkspaceSettings"));
const KBLayout = React.lazy(() => import("../../screens/KnowledgeBase"));
const QuizzesBase = React.lazy(() => import("../../screens/Quizzes/QuizzesBase"));
const AcknowledgmentsOverview = React.lazy(() => import("../../screens/AcknowledgmentsOverview"));
const TaskLists = React.lazy(() => import("../../screens/Lists/TaskLists"));


import {PAGES} from "@stores/ViewStore";


export const MainContentRouter = observer(() => {
    const rootStore = useRootStore();
    const userState = useUserState();
    const page = rootStore.view.page;

    // Knowledge Base pages continue to use KnowledgeBase screen
    if ([PAGES.KNOWLEDGE_BASE, PAGES.DOC_EDIT, PAGES.DOC_HISTORY].includes(page as any)) {
        return (
            <Suspense fallback={<Loading/>}>
                <KBLayout/>
            </Suspense>
        );
    }

    let content: ReactNode | null = null;
    const isCurrentUserAdmin = userState?.isCurrentUserAdmin;

    switch (page) {
        case PAGES.SETTINGS_GENERAL:
            content = (
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <Settings/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_MEMBERS:
            content = (
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <Members/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_BILLING:
            content = (
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <PlansAndBilling/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.WORKSPACE_SETTINGS_GENERAL:
            content = (
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <WorkspaceSettings key={rootStore.view.workspace?.id}/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.WORKSPACE_MEMBERS:
            content = (
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <WsMembers key={rootStore.view.workspace?.id}/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_PROFILE:
            content = (
                <Suspense fallback={<LoadingCircle/>}>
                    <Profile/>
                </Suspense>
            );
            break;
        case PAGES.QUIZZES:
        case PAGES.QUIZ_VIEW:
        case PAGES.QUIZ_EDIT:
        case PAGES.QUIZ_TAKE:
            content = (
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    <QuizzesBase/>
                </Suspense>
            );
            break;
        case PAGES.ACKNOWLEDGMENTS_OVERVIEW:
            content = (
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    <AcknowledgmentsOverview/>
                </Suspense>
            );
            break;
        case PAGES.LISTS:
            content = (
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    {rootStore.view.workspace ? (
                        <TaskLists wsId={rootStore.view.workspace!.id}/>
                    ) : null}
                </Suspense>
            );
            break;
        case PAGES.CHARTFLOW:
            content = (
                <Suspense fallback={<LoadingCircle/>}>
                    <Chartflow/>
                </Suspense>
            );
            break;
        default:
            content = null;
    }

    return content;
});

const NoAdminAccessRightMessage = () => (
    <PageSurface>
        <SectionMessage
            className={'PageSurface__width'}
            variant={'warning'}
            title={intl.get('settings-menu.no-right_header').d('You do not have access rights')}
            message={intl.get('settings-menu.no-right_body').d('To view or modify this, you need to have Admin or Owner privileges. If access is required, please reach out to the Owner or Admin.')}
        />
    </PageSurface>
);
